<template>
  <div>
    <!-- Drawing selector -->
    <v-select
      v-model="selectedDrawingId"
      :items="drawings"
      :label="$t('miniMap.selectDrawing') || 'Select Drawing'"
      item-title="Title"
      item-value="Id"
      density="compact"
      variant="outlined"
      class="mb-4"
    />

    <!-- Location selector -->
    <v-combobox
      v-model="selectedLocation"
      :items="locations"
      :label="$t('miniMap.selectLocation') || 'Select Location'"
      item-title="Title"
      item-value="Id"
      density="compact"
      variant="outlined"
      clearable
      return-object
      class="mt-4"
    />

    <!-- Map container -->
    <div class="map-container" ref="mapContainer">
      <img
        v-if="currentDrawing?.Url"
        :src="currentDrawing.Url"
        @load="onImageLoad"
        @click="onMapClick"
        class="drawing-image"
        ref="drawingImage"
      />
      
      <!-- Location pins -->
      <div
        v-for="location in locations"
        :key="location.Id"
        class="location-pin"
        :style="{
          left: `${location.X * imageScale}px`,
          top: `${location.Y * imageScale}px`
        }"
        @click.stop="selectLocation(location)"
        :class="{ 'selected': selectedLocation?.Id === location.Id }"
      >
        <img 
          src="/assets/location-pin.svg"
          class="location-pin-icon"
          :class="{ 'selected-pin': selectedLocation?.Id === location.Id }"
          width="26"
          height="34"
        />
        <v-tooltip location="top" activator="parent">
          {{ location.Title }}
        </v-tooltip>
      </div>
      
      <!-- Drilldown pins -->
      <div
        v-for="drilldown in drilldowns"
        :key="drilldown.Id"
        class="drilldown-pin"
        :style="{
          left: `${drilldown.X * imageScale}px`,
          top: `${drilldown.Y * imageScale}px`
        }"
        @click.stop="navigateToDrilldown(drilldown)"
      >
        <img 
          src="/assets/drilldown-icon.svg"
          class="drilldown-icon"
          width="26"
          height="26"
        />
        <v-tooltip location="top" activator="parent">
          {{ $t('miniMap.drilldownTo') || 'Drill down to' }}: {{ drilldown.LinkedDrawingTitle }}
        </v-tooltip>
      </div>
    </div>

    
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'

// Props
const props = defineProps({
  drawingId: {
    type: [String, Number],
    default: null
  },
  selectedSubtask: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['location-selected', 'drawing-changed'])

// Store
const store = useStore()
const conn = store.getters.getDBConnection

// Refs
const mapContainer = ref(null)
const drawingImage = ref(null)

// Data
const drawings = ref([])
const locations = ref([])
const drilldowns = ref([])
const selectedDrawingId = ref(null)
const selectedLocation = ref(null)
const imageScale = ref(1)
const defaultDrawingId = ref(null)

// Computed
const currentDrawing = computed(() => {
  return drawings.value.find(d => d.Id === selectedDrawingId.value)
})

// Methods
const loadDrawings = async () => {
  try {
    const query = `
      SELECT Id, Title, Url, ThumbnailUrl, IsDefault, Sort
      FROM drawings
      ORDER BY Sort, Title
    `
    const result = await conn.query(query)
    drawings.value = result.toArray().map(row => row.toJSON())
    
    // Find default drawing
    const defaultDrawing = drawings.value.find(d => d.IsDefault === 1 || d.IsDefault === true)
    defaultDrawingId.value = defaultDrawing?.Id || (drawings.value[0]?.Id || null)
  } catch (error) {
    console.error('Error loading drawings:', error)
  }
}

const loadLocations = async (drawingId) => {
  if (!drawingId) {
    locations.value = []
    return
  }
  
  try {
    const query = `
      SELECT Id, X, Y, Title, DrawingId, ExternalReference
      FROM locations
      WHERE DrawingId = '${drawingId}'
      ORDER BY Title
    `
    const result = await conn.query(query)
    locations.value = result.toArray().map(row => row.toJSON())
  } catch (error) {
    console.error('Error loading locations:', error)
  }
}

const loadDrilldowns = async (drawingId) => {
  if (!drawingId) {
    drilldowns.value = []
    return
  }
  
  try {
    const query = `
      SELECT Id, X, Y, DrawingId, LinkedDrawingId, LinkedDrawingTitle
      FROM drilldowns
      WHERE DrawingId = '${drawingId}'
      ORDER BY LinkedDrawingTitle
    `
    const result = await conn.query(query)
    drilldowns.value = result.toArray().map(row => row.toJSON())
  } catch (error) {
    console.error('Error loading drilldowns:', error)
  }
}

const onImageLoad = () => {
  calculateImageScale()
}

const calculateImageScale = () => {
  if (!drawingImage.value || !mapContainer.value) return
  
  const containerWidth = mapContainer.value.clientWidth
  const imageNaturalWidth = drawingImage.value.naturalWidth
  
  if (imageNaturalWidth > 0) {
    imageScale.value = containerWidth / imageNaturalWidth
  }
}

const selectLocation = (location) => {
  selectedLocation.value = location
  emit('location-selected', location)
}

const onMapClick = (event) => {
  // Get click coordinates relative to image
  const rect = drawingImage.value.getBoundingClientRect()
  const x = (event.clientX - rect.left) / imageScale.value
  const y = (event.clientY - rect.top) / imageScale.value
  
  // Find nearest location within threshold
  const threshold = 20 // pixels
  let nearestLocation = null
  let nearestDistance = threshold
  
  locations.value.forEach(location => {
    const distance = Math.sqrt(
      Math.pow(location.X - x, 2) + 
      Math.pow(location.Y - y, 2)
    )
    
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestLocation = location
    }
  })
  
  if (nearestLocation) {
    selectLocation(nearestLocation)
  } else {
    selectedLocation.value = null
  }
}

const navigateToDrilldown = (drilldown) => {
  if (drilldown.LinkedDrawingId) {
    selectedDrawingId.value = drilldown.LinkedDrawingId
  }
}



// Watchers
watch(() => props.drawingId, (newVal) => {
  if (newVal) {
    selectedDrawingId.value = newVal
  }
}, { immediate: true })

watch(selectedDrawingId, async (newVal) => {
  if (newVal) {
    await Promise.all([
      loadLocations(newVal),
      loadDrilldowns(newVal)
    ])
    selectedLocation.value = null
    emit('drawing-changed', newVal)
  }
})

// Initialize component
onMounted(async () => {
  if (drawings.value.length === 0) {
    await loadDrawings()
    
    // Set initial drawing
    if (props.drawingId) {
      selectedDrawingId.value = props.drawingId
    } else if (!selectedDrawingId.value) {
      selectedDrawingId.value = defaultDrawingId.value
    }
  }
})

// Handle window resize
window.addEventListener('resize', calculateImageScale)

// Cleanup
onUnmounted(() => {
  window.removeEventListener('resize', calculateImageScale)
})
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawing-image {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  width: auto;
  display: block;
  cursor: crosshair;
  object-fit: contain;
}

.location-pin {
  position: absolute;
  transform: translate(-50%, -100%);
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.location-pin:hover {
  transform: translate(-50%, -100%) scale(1.1);
  z-index: 20;
}

.location-pin.selected {
  transform: translate(-50%, -100%) scale(1.2);
  z-index: 30;
}

.location-pin-icon {
  display: block;
  transition: all 0.2s ease;
}

.location-pin-icon.selected-pin {
  filter: hue-rotate(-50deg) saturate(1.5) brightness(0.9);
}

.drilldown-pin {
  position: absolute;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 15;
}

.drilldown-pin:hover {
  transform: translate(-50%, -50%) scale(1.1);
  z-index: 25;
}

.drilldown-icon {
  display: block;
  transition: all 0.2s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.drilldown-pin:hover .drilldown-icon {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4));
}
</style>